import { useCallback, useEffect, useRef, useState } from "react";
import { AUTH_NOTE_ID } from "~/components/auth/tax-auth-shell";
import { Icon as CatalogIcon } from "~/components/ui/icon";
import { type MessageKey, type MessageParams, useI18n } from "~/i18n";
import { useAuth } from "~/lib/auth/auth-context";
import { AuthError } from "~/lib/auth/errors";
import {
  type GoogleProvider,
  beginGoogleSignIn,
  isGoogleCallbackOrigin,
  takeGoogleSignInNotice,
} from "~/lib/auth/google";
import {
  type SignInMethod,
  readLastSignInMethod,
  recordLastSignInMethod,
} from "~/lib/auth/last-sign-in";
import {
  PASSKEY_AUTOFILL_REFRESH_MS,
  isConditionalMediationAvailable,
  isTaxPasskeyHost,
  isWebAuthnSupported,
} from "~/lib/auth/passkeys";

type BusyAction = "password" | "passkey" | "google" | "resetRequest" | null;
/** Google's slot: held while `/v1/auth/providers` answers, so nothing jumps.
 * `ready` with no provider means Google is not offered here (disabled, or a
 * different origin); `failed` means the lookup itself failed and can retry. */
type GoogleSlot =
  | { state: "loading" }
  | { state: "ready"; provider: GoogleProvider | null }
  | { state: "failed" };
/** Messages are kept as catalog keys, not rendered strings, so switching the
 * language while one is on screen re-renders it in the new language. */
type Message = { key: MessageKey; params?: MessageParams };
type Feedback = { tone: "critical" | "neutral"; message: Message };
type FieldErrors = { email?: MessageKey; password?: MessageKey };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GOOGLE_NOTICE_KEYS: Record<string, MessageKey> = {
  notInvited: "auth.error.googleNotInvited",
  rejected: "auth.error.googleRejected",
  unavailable: "auth.error.googleUnavailable",
  cancelled: "auth.error.googleCancelled",
};

const PASSKEY_ERROR_KEYS: Record<string, MessageKey> = {
  unsupported: "auth.error.passkeyUnsupported",
  cancelled: "auth.error.passkeyCancelled",
  origin: "auth.error.passkeyOrigin",
  unavailable: "auth.error.passkeyUnavailable",
  timeout: "auth.error.passkeyTimeout",
};

/**
 * In-app Tax Platform sign-in. Google and passkeys lead; email + password is
 * revealed on demand. Every method goes to the Tax Admin API and the browser
 * never leaves the product shell except for Google's own consent screen.
 */
export function TaxSignInForm() {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const mock = auth.mode === "mock";
  const [view, setView] = useState<"options" | "email">("options");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busyAction, setBusyAction] = useState<BusyAction>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [resetOpen, setResetOpen] = useState(false);
  const [resetNotice, setResetNotice] = useState<MessageKey | null>(null);
  const [lastMethod] = useState<SignInMethod | null>(() => readLastSignInMethod());
  const [google, setGoogle] = useState<GoogleSlot>({ state: "loading" });
  const [providersAttempt, setProvidersAttempt] = useState(0);
  const busy = busyAction !== null;
  const setError = useCallback(
    (message: Message | null) => setFeedback(message ? { tone: "critical", message } : null),
    [],
  );

  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const emailTriggerRef = useRef<HTMLButtonElement>(null);
  const resetTitleRef = useRef<HTMLHeadingElement>(null);
  const forgotRef = useRef<HTMLButtonElement>(null);
  const noticeRef = useRef<HTMLOutputElement>(null);
  const focusAfterViewChange = useRef(false);
  const passkeyAutofillAbortRef = useRef<AbortController | null>(null);

  // A Google redirect that failed (wrong domain, cancelled…) lands back here
  // with its reason so the operator can retry without leaving the card.
  useEffect(() => {
    const notice = takeGoogleSignInNotice();
    if (notice) setError({ key: GOOGLE_NOTICE_KEYS[notice] ?? "auth.error.google" });
  }, [setError]);

  const { listSignInProviders } = auth;
  useEffect(() => {
    let cancelled = false;
    // Retry re-enters here with a fresh attempt number.
    void providersAttempt;
    if (mock) {
      // Mock mode has no Google client; show the slot so the screen matches
      // production and signs straight in when pressed.
      setGoogle({
        state: "ready",
        provider: { clientId: "mock", redirectUri: `${window.location.origin}/auth/callback` },
      });
      return;
    }
    void listSignInProviders()
      .then((providers) => {
        if (cancelled) return;
        // Only on the origin Google redirects back to; elsewhere the button
        // would strand the PKCE transaction in another origin's storage.
        setGoogle({
          state: "ready",
          provider:
            providers.google && isGoogleCallbackOrigin(providers.google) ? providers.google : null,
        });
      })
      .catch(() => {
        // A failed lookup is not "Google disabled": say so and offer a retry.
        if (!cancelled) setGoogle({ state: "failed" });
      });
    return () => {
      cancelled = true;
    };
  }, [listSignInProviders, mock, providersAttempt]);

  function retryProviders() {
    setGoogle({ state: "loading" });
    setProvidersAttempt((attempt) => attempt + 1);
  }

  // Tax-owned passkeys use RP ID tax.backchannel.com.br (localhost locally).
  // Elsewhere (the Firebase rollback host) the ceremony cannot succeed, so the
  // option is not offered at all.
  const passkeyAvailable =
    mock || (auth.mode === "platform" && isWebAuthnSupported() && isTaxPasskeyHost());
  const googleProvider = google.state === "ready" ? google.provider : null;
  const hasAlternatives =
    google.state === "loading" ||
    google.state === "failed" ||
    googleProvider !== null ||
    passkeyAvailable;
  // With nothing else to offer, the email form is the whole card.
  const emailOnly = !hasAlternatives;
  const showEmail = view === "email" || emailOnly;

  useEffect(() => {
    if (!focusAfterViewChange.current) return;
    focusAfterViewChange.current = false;
    // Move focus with the view: into the email field, or back to its trigger.
    if (view === "email") emailInputRef.current?.focus();
    else emailTriggerRef.current?.focus();
  }, [view]);

  // WebAuthn conditional mediation: while the email field is on screen,
  // offer the operator's saved passkeys in its own autofill dropdown. Kept
  // to the email view on purpose — the options view has no text input to
  // anchor autocomplete="webauthn" to, and a hidden input just to start the
  // request earlier would fight the "keep the autofill field visible" best
  // practice for no real benefit, since "Entrar com passkey" already leads
  // the options view as an explicit, one-tap alternative.
  useEffect(() => {
    if (mock || !passkeyAvailable || view !== "email") return;
    let cancelled = false;

    async function loop() {
      const available = await isConditionalMediationAvailable();
      if (cancelled || !available) return;
      while (!cancelled) {
        const controller = new AbortController();
        passkeyAutofillAbortRef.current = controller;
        const refreshTimer = window.setTimeout(() => {
          // The server's single-use challenge expires before an operator
          // who leaves the dropdown open indefinitely might pick from it;
          // restart with a fresh one rather than let a stale pick fail.
          controller.abort();
        }, PASSKEY_AUTOFILL_REFRESH_MS);

        try {
          await auth.signInWithPasskeyAutofill(controller.signal);
          recordLastSignInMethod("passkey");
          return;
        } catch (cause) {
          if (cancelled) return;
          if (!(cause instanceof AuthError && cause.code === "cancelled")) {
            // A genuine ceremony/network failure, not our own abort. This is
            // a passive background offer the operator never explicitly
            // triggered, so fail quietly — the passkey button still works.
            return;
          }
          // Our refresh timeout (or the operator dismissing the suggestion
          // without picking one, which some browsers also report as an
          // abort): loop with a fresh challenge.
        } finally {
          window.clearTimeout(refreshTimer);
          if (passkeyAutofillAbortRef.current === controller) {
            passkeyAutofillAbortRef.current = null;
          }
        }
      }
    }

    void loop();
    return () => {
      cancelled = true;
      passkeyAutofillAbortRef.current?.abort();
      passkeyAutofillAbortRef.current = null;
    };
  }, [auth, mock, passkeyAvailable, view]);

  function openEmail() {
    setError(null);
    setFieldErrors({});
    focusAfterViewChange.current = true;
    setView("email");
  }

  function backToOptions() {
    if (busy) return;
    setError(null);
    setFieldErrors({});
    setResetOpen(false);
    setResetNotice(null);
    focusAfterViewChange.current = true;
    setView("options");
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The operator chose password sign-in explicitly; stop offering the
    // autofill suggestion so it cannot race the password ceremony.
    passkeyAutofillAbortRef.current?.abort();
    setError(null);
    setResetNotice(null);
    const trimmed = email.trim();
    const nextErrors: FieldErrors = {};
    if (!trimmed) nextErrors.email = "auth.error.emailRequired";
    else if (!EMAIL_PATTERN.test(trimmed)) nextErrors.email = "auth.error.emailInvalid";
    if (!password) nextErrors.password = "auth.error.passwordRequired";
    setFieldErrors(nextErrors);
    if (nextErrors.email || nextErrors.password) {
      if (nextErrors.email) emailInputRef.current?.focus();
      else passwordInputRef.current?.focus();
      return;
    }
    setBusyAction("password");

    try {
      await auth.signInWithPassword(email, password);
      recordLastSignInMethod("email");
    } catch (cause: unknown) {
      if (cause instanceof AuthError && cause.reason === "challenge") {
        setError({ key: "auth.error.challenge" });
      } else if (
        cause instanceof AuthError &&
        cause.reason === "credentials" &&
        cause.code === "PasswordResetRequiredException"
      ) {
        setError({ key: "auth.error.passwordResetRequired" });
      } else if (cause instanceof AuthError && cause.reason === "config") {
        setError({ key: "auth.error.config", params: { reason: cause.message } });
      } else if (cause instanceof AuthError && cause.reason === "network") {
        setError({ key: "auth.error.network" });
      } else {
        // Keep credential failures intentionally generic; do not reveal which
        // operator accounts exist.
        setError({ key: "auth.error.credentials" });
      }
    } finally {
      setBusyAction(null);
    }
  }

  async function submitPasskey() {
    if (!passkeyAvailable) return;
    // Explicit request; the conditional autofill attempt would otherwise
    // hold its own concurrent WebAuthn ceremony open.
    passkeyAutofillAbortRef.current?.abort();
    setError(null);
    setBusyAction("passkey");

    try {
      // Discoverable credential: the authenticator chooses the account, so
      // no email is needed on this path.
      await auth.signInWithPasskey(view === "email" ? email : "");
      recordLastSignInMethod("passkey");
    } catch (cause: unknown) {
      const code = cause instanceof AuthError && cause.reason === "passkey" ? cause.code : null;
      const message: Message = { key: PASSKEY_ERROR_KEYS[code ?? ""] ?? "auth.error.passkey" };
      // Dismissing the prompt is a choice, not a failure: say it neutrally.
      setFeedback({ tone: code === "cancelled" ? "neutral" : "critical", message });
    } finally {
      setBusyAction(null);
    }
  }

  async function submitGoogle() {
    if (!googleProvider) return;
    passkeyAutofillAbortRef.current?.abort();
    setError(null);
    setBusyAction("google");
    if (mock) {
      await auth.signIn();
      recordLastSignInMethod("google");
      setBusyAction(null);
      return;
    }
    // A real Google sign-in is recorded by the callback once it completes.
    try {
      await beginGoogleSignIn(googleProvider, { locale });
      // The browser is navigating to Google; keep the busy state.
    } catch {
      setError({ key: "auth.error.google" });
      setBusyAction(null);
    }
  }

  async function requestReset() {
    setError(null);
    setResetNotice(null);
    if (!email.trim()) {
      setError({ key: "auth.error.resetEmailRequired" });
      emailInputRef.current?.focus();
      return;
    }
    setBusyAction("resetRequest");
    try {
      await auth.requestPasswordReset(email, locale);
      setResetOpen(false);
      setResetNotice("auth.resetSent");
    } catch (cause: unknown) {
      setError({
        key:
          cause instanceof AuthError && cause.reason === "network"
            ? "auth.error.network"
            : "auth.error.reset",
      });
    } finally {
      setBusyAction(null);
    }
  }

  const closeReset = useCallback(() => {
    if (busyAction !== null) return;
    setResetOpen(false);
    setResetNotice(null);
    setError(null);
    forgotRef.current?.focus();
  }, [busyAction, setError]);

  useEffect(() => {
    if (!resetOpen) return;
    resetTitleRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeReset();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [resetOpen, closeReset]);

  useEffect(() => {
    if (!resetNotice || resetOpen) return;
    noticeRef.current?.focus();
  }, [resetNotice, resetOpen]);

  const alert = feedback && (
    <div
      className={`tax-sign-in__alert tax-sign-in__alert--${feedback.tone}`}
      role={feedback.tone === "critical" ? "alert" : "status"}
    >
      <CatalogIcon name={feedback.tone === "critical" ? "error" : "info"} size={16} />
      <p>{t(feedback.message.key, feedback.message.params)}</p>
    </div>
  );
  const lastUsedLine = (method: SignInMethod) =>
    lastMethod === method ? (
      <p id={`tax-last-${method}`} className="tax-sign-in__last">
        <CatalogIcon name="check" size={16} />
        {t("auth.lastUsed")}
      </p>
    ) : null;
  const lastUsedRef = (method: SignInMethod) =>
    lastMethod === method ? `tax-last-${method}` : undefined;

  if (!showEmail) {
    return (
      <div key="options" className="tax-sign-in tax-sign-in--options" data-view="options">
        {alert}
        <div className="tax-sign-in__methods" aria-busy={google.state === "loading" || undefined}>
          {google.state === "loading" ? (
            <div className="tax-sign-in__skeleton" data-testid="google-skeleton">
              <output className="sr-only">{t("auth.providersLoading")}</output>
            </div>
          ) : google.state === "failed" ? (
            <div className="tax-sign-in__slot-failed">
              <CatalogIcon name="info" size={16} />
              <p>{t("auth.providersFailed")}</p>
              <button type="button" className="tax-sign-in__text-button" onClick={retryProviders}>
                <CatalogIcon name="refresh" size={16} />
                {t("common.retry")}
              </button>
            </div>
          ) : (
            googleProvider && (
              <div className="tax-sign-in__method">
                <button
                  type="button"
                  className="tax-sign-in__google"
                  onClick={() => void submitGoogle()}
                  disabled={busy}
                  aria-busy={busyAction === "google"}
                  aria-describedby={
                    [AUTH_NOTE_ID, lastUsedRef("google")].filter(Boolean).join(" ") || undefined
                  }
                >
                  {busyAction === "google" ? <Spinner /> : <GoogleMark />}
                  <span>
                    {busyAction === "google" ? t("auth.googleRedirecting") : t("auth.google")}
                  </span>
                </button>
                {lastUsedLine("google")}
              </div>
            )
          )}

          {passkeyAvailable && (
            <div className="tax-sign-in__method">
              <button
                type="button"
                className="tax-sign-in__option tax-sign-in-form__passkey"
                onClick={() => void submitPasskey()}
                disabled={busy}
                aria-busy={busyAction === "passkey"}
                aria-describedby={lastUsedRef("passkey")}
              >
                {busyAction === "passkey" ? <Spinner /> : <PasskeyIcon />}
                <span>
                  {busyAction === "passkey" ? t("auth.passkeySigningIn") : t("auth.passkey")}
                </span>
              </button>
              {lastUsedLine("passkey")}
            </div>
          )}
        </div>

        <div className="tax-sign-in__divider" role="presentation">
          <span>{t("auth.or")}</span>
        </div>

        <div className="tax-sign-in__method">
          <button
            type="button"
            ref={emailTriggerRef}
            className="tax-sign-in__option tax-sign-in__option--email"
            onClick={openEmail}
            disabled={busy}
            aria-describedby={lastUsedRef("email")}
          >
            <MailIcon />
            <span>{t("auth.continueWithEmail")}</span>
            <CatalogIcon name="arrowRight" size={16} className="tax-sign-in__next" />
          </button>
          {lastUsedLine("email")}
        </div>

        {googleProvider && <p className="tax-sign-in__footnote">{t("auth.googleReaderNote")}</p>}
      </div>
    );
  }

  return (
    <div key="email" className="tax-sign-in tax-sign-in--email" data-view="email">
      {alert}

      <form
        id="tax-sign-in-email-form"
        className="tax-sign-in-form"
        onSubmit={submit}
        noValidate
        aria-label={t("auth.emailFormLabel")}
      >
        <div className="field">
          <label className="field__label" htmlFor="tax-sign-in-email">
            {t("auth.email")}
          </label>
          <input
            ref={emailInputRef}
            id="tax-sign-in-email"
            className="input"
            type="email"
            name="email"
            inputMode="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (fieldErrors.email)
                setFieldErrors((current) => ({ ...current, email: undefined }));
            }}
            aria-invalid={fieldErrors.email ? true : undefined}
            aria-describedby={fieldErrors.email ? "tax-sign-in-email-error" : undefined}
            autoComplete="username webauthn"
            autoCapitalize="none"
            spellCheck={false}
            placeholder={t("auth.emailPlaceholder")}
            required
            disabled={busy}
          />
          <FieldError
            id="tax-sign-in-email-error"
            message={fieldErrors.email && t(fieldErrors.email)}
          />
        </div>

        <div className="field">
          <div className="tax-sign-in__label-row">
            <label className="field__label" htmlFor="tax-sign-in-password">
              {t("auth.password")}
            </label>
            {auth.mode === "platform" && (
              <button
                type="button"
                ref={forgotRef}
                className="tax-sign-in-form__forgot"
                onClick={() => {
                  setResetOpen(true);
                  setResetNotice(null);
                  setError(null);
                }}
                disabled={busy}
              >
                {t("auth.forgotPassword")}
              </button>
            )}
          </div>
          <div className="tax-sign-in-form__password-field" data-replay-block>
            <input
              ref={passwordInputRef}
              id="tax-sign-in-password"
              className="input"
              type={showPassword ? "text" : "password"}
              name="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (fieldErrors.password) {
                  setFieldErrors((current) => ({ ...current, password: undefined }));
                }
              }}
              aria-invalid={fieldErrors.password ? true : undefined}
              aria-describedby={fieldErrors.password ? "tax-sign-in-password-error" : undefined}
              autoComplete="current-password"
              required
              disabled={busy}
            />
            <button
              type="button"
              className="tax-sign-in-form__password-toggle"
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((visible) => !visible)}
              disabled={busy}
            >
              <EyeIcon crossed={showPassword} />
            </button>
          </div>
          <FieldError
            id="tax-sign-in-password-error"
            message={fieldErrors.password && t(fieldErrors.password)}
          />
        </div>

        <button
          type="submit"
          className="btn btn--primary tax-auth-shell__primary"
          disabled={busy}
          aria-busy={busyAction === "password"}
        >
          {busyAction === "password" && <Spinner inverse />}
          {busyAction === "password" ? t("auth.signingIn") : t("auth.signIn")}
        </button>
      </form>

      {!emailOnly && (
        <button type="button" className="tax-sign-in__back" onClick={backToOptions} disabled={busy}>
          <CatalogIcon name="back" size={16} />
          {t("auth.otherOptions")}
        </button>
      )}

      {resetNotice && (
        <output ref={noticeRef} tabIndex={-1} className="tax-sign-in__notice">
          <CheckIcon />
          <span>{t(resetNotice)}</span>
        </output>
      )}

      {resetOpen && (
        <section className="tax-sign-in-form__reset" aria-labelledby="tax-reset-title">
          <div className="tax-sign-in-form__reset-heading">
            <h2 id="tax-reset-title" ref={resetTitleRef} tabIndex={-1}>
              {t("auth.resetPassword")}
            </h2>
            <button
              type="button"
              className="tax-sign-in-form__reset-close"
              onClick={closeReset}
              disabled={busy}
            >
              {t("common.close")}
            </button>
          </div>
          <p>{t("auth.resetPasswordDescription")}</p>
          <button
            type="button"
            className="btn tax-sign-in-form__reset-action"
            onClick={() => void requestReset()}
            disabled={busy}
          >
            {busyAction === "resetRequest" ? t("auth.resetting") : t("auth.sendResetLink")}
          </button>
          <button
            type="button"
            className="tax-sign-in-form__reset-back"
            onClick={closeReset}
            disabled={busy}
          >
            {t("auth.backToSignIn")}
          </button>
        </section>
      )}
    </div>
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="tax-sign-in__field-error">
      <CatalogIcon name="error" size={16} />
      {message}
    </p>
  );
}

function Spinner({ inverse = false }: { inverse?: boolean }) {
  return (
    <span
      className={`tax-sign-in__spinner${inverse ? " tax-sign-in__spinner--inverse" : ""}`}
      aria-hidden="true"
    />
  );
}

function Icon({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <svg
      className={`tax-sign-in__icon${className ? ` ${className}` : ""}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function MailIcon() {
  return (
    <Icon>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </Icon>
  );
}

/** Passkey: a plain key, drawn at the same 20px box and stroke as e-mail. */
function PasskeyIcon() {
  return (
    <Icon>
      <circle cx="7.5" cy="12" r="4" />
      <path d="M11.5 12H21m-3 0v3.5M15 12v2.5" />
    </Icon>
  );
}

function CheckIcon() {
  return (
    <Icon className="tax-sign-in__notice-icon">
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12.3 2.7 2.7L16 9.7" />
    </Icon>
  );
}

function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2.5 12s3.2-5 9.5-5 9.5 5 9.5 5-3.2 5-9.5 5-9.5-5-9.5-5Z" />
      <circle cx="12" cy="12" r="2.5" />
      {crossed && <path d="M4 4l16 16" />}
    </svg>
  );
}

/**
 * Google's standard "G" mark, as supplied with the current Sign in with Google
 * branding assets (48×48 artwork, four flat colours). Use it unmodified.
 * https://developers.google.com/identity/branding-guidelines
 */
function GoogleMark() {
  return (
    <svg
      className="tax-sign-in__google-mark"
      viewBox="0 0 48 48"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
